import './style.css';
import { I18n } from './i18n.js';

/**
 * Pindai Chat Widget v3.0
 * - Agent-API integration (HMAC auth, SSE streaming, human-agent handoff)
 * - Expanded customization (avatar, bubble, dark theme, custom footer, button alignment)
 * - Full backward compatibility with generic webhookUrl
 */
class PindaiChatWidget {
  constructor(options) {
    // ── Agent-API mode (new) ──────────────────────────────────────────────
    this.agentId = options.agentId || null;
    this.embedSecret = options.embedSecret || null;
    this.apiBaseUrl = options.apiBaseUrl
      ? options.apiBaseUrl.replace(/\/$/, '')
      : null;

    // ── Legacy generic-webhook mode ───────────────────────────────────────
    const legacyEndpoint = options.webhookUrl || options.n8nUrl;

    // Validation: one of the two modes must be configured
    const isAgentMode = !!(this.agentId && this.embedSecret && this.apiBaseUrl);
    const isLegacyMode = !!legacyEndpoint;

    if (!isAgentMode && !isLegacyMode) {
      throw new Error(
        'PindaiChatWidget: Provide either (agentId + embedSecret + apiBaseUrl) ' +
        'for Pindai Agent-API mode, or "webhookUrl" for generic webhook mode.'
      );
    }

    this.webhookUrl = isAgentMode
      ? `${this.apiBaseUrl}/v1/chat/${this.agentId}/message`
      : legacyEndpoint;

    this._agentMode = isAgentMode;
    this._embedToken = null; // cached after first HMAC computation

    // ── Display ───────────────────────────────────────────────────────────
    this.mode = options.mode || 'widget';

    // ── Internationalization ──────────────────────────────────────────────
    this.locale = options.locale || 'id';
    this.i18n = new I18n(this.locale);

    // ── UI customization ──────────────────────────────────────────────────
    this.title = options.title || this.i18n.t('title');
    this.initialMessage = options.initialMessage || this.i18n.t('initialMessage');

    // Launcher icon (support custom URL or default SVG)
    this.launcherIconUrl = options.launcherIconUrl || this._getDefaultLauncherIcon();

    // ── Branding ──────────────────────────────────────────────────────────
    this.logoUrl = options.logoUrl || 'https://pindai.ai/logo.png';
    this.showLogo = options.showLogo !== false;
    this.avatarUrl = options.avatarUrl || null;

    this.accentColor = options.accentColor || '#2563eb';
    // If launcher/send colors are not provided, inherit the accent color.
    this.launcherColor = options.launcherColor || this.accentColor;
    this.sendButtonColor = options.sendButtonColor || this.accentColor;

    // ── Theme ─────────────────────────────────────────────────────────────
    // 'light' | 'dark' | 'auto'
    this.theme = options.theme || 'light';

    // ── Button alignment ──────────────────────────────────────────────────
    // 'bottom-right' | 'bottom-left'
    this.buttonAlignment = options.buttonAlignment || 'bottom-right';

    // ── Bubble (speech bubble above launcher) ────────────────────────────
    // Normalize: bubbleMessages takes priority; bubbleText is a shorthand for a single message.
    const rawMessages = options.bubbleMessages
      || (options.bubbleText ? [options.bubbleText] : null);
    this.bubbleMessages = rawMessages && rawMessages.length > 0 ? rawMessages : null;
    // Delay before bubble first appears (ms)
    this.bubbleDelay = typeof options.bubbleDelay === 'number' ? options.bubbleDelay : 3000;
    // Interval between rotating messages when bubbleMessages has >1 entry (ms)
    this.bubbleInterval = typeof options.bubbleInterval === 'number' ? options.bubbleInterval : 5000;
    // If true (default), remember the dismiss and never show again for this session
    this.showBubbleOnce = options.showBubbleOnce !== false;
    // Track if caller explicitly set showBubbleOnce so remote config won't override it
    this._showBubbleOnceExplicit = 'showBubbleOnce' in options;
    this._bubbleDismissed = false;
    this._bubbleMsgIndex = 0;
    this._bubbleRotateTimer = null;
    this._bubbleDelayTimer = null;
    this._actionLabelTimer = null;

    // ── Footer / Branding ─────────────────────────────────────────────────
    this.customFooter = options.customFooter || null;
    this.showBranding = options.showBranding !== false;

    // ── Action indicators ─────────────────────────────────────────────────
    this.showActionIndicators = options.showActionIndicators !== false;

    // ── Visitor info (forwarded to API) ───────────────────────────────────
    this.visitorName = options.visitorName || null;
    this.visitorEmail = options.visitorEmail || null;

    // ── Streaming ─────────────────────────────────────────────────────────
    // SSE streaming is only available in agent-api mode
    this.enableStreaming = this._agentMode && options.enableStreaming !== false;

    // ── File upload ───────────────────────────────────────────────────────
    this.enableFileUpload = options.enableFileUpload !== false;
    this.allowedFileTypes = options.allowedFileTypes || [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    this.maxFileSize = options.maxFileSize || 10 * 1024 * 1024; // 10 MB
    this.maxFiles = options.maxFiles || 5;
    this.uploadedFiles = [];

    // ── Notifications ─────────────────────────────────────────────────────
    this.enableNotifications = options.enableNotifications !== false;
    this.enableSound = options.enableSound === true;
    this.unreadCount = 0;

    // ── Lead capture ──────────────────────────────────────────────────────
    this.leadCollectionEnabled = options.leadCollectionEnabled === true;
    this.leadFormTrigger = options.leadFormTrigger || 'pre_chat'; // 'pre_chat' | 'in_chat'
    this.leadFormTitle = options.leadFormTitle || null;
    this.leadFormFields = options.leadFormFields || ['name', 'email', 'phone'];
    this.leadAllowSkip = options.leadAllowSkip !== false;
    this.leadSubmitButtonText = options.leadSubmitButtonText || null;
    // Per-session state: track whether the form was already submitted/skipped.
    // Uses sessionStorage so the form reappears in each new browser tab/session.
    const _leadStateKey = this._agentMode ? `pindai-lead-done-${this.agentId}` : null;
    this._leadDone = _leadStateKey
      ? (() => { try { return !!sessionStorage.getItem(_leadStateKey); } catch (_) { return false; } })()
      : false;
    this._leadStateKey = _leadStateKey;

    // ── Quick replies ─────────────────────────────────────────────────────
    this.showQuickReplies = options.showQuickReplies !== false;
    this.quickReplies = options.quickReplies || [
      this.i18n.t('quickReply1'),
      this.i18n.t('quickReply2'),
      this.i18n.t('quickReply3'),
      this.i18n.t('quickReply4')
    ];

    // ── Message history ───────────────────────────────────────────────────
    this.enableHistory = options.enableHistory !== false;
    this.maxHistoryItems = options.maxHistoryItems || 50;
    const historyKey = this._agentMode ? this.agentId : this.webhookUrl;
    this.historyKey = `pindai-chat-history-${historyKey}`;
    this.stateKey = `pindai-chat-state-${historyKey}`;

    // ── Error handling & retry ────────────────────────────────────────────
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 1000;
    this.requestTimeout = options.requestTimeout || 30000;

    // ── Rate limiting ─────────────────────────────────────────────────────
    this.rateLimit = options.rateLimit || 5;
    this.rateLimitWindow = options.rateLimitWindow || 60000;
    this.messageTimes = [];

    // ── DOM references ────────────────────────────────────────────────────
    this.container = null;
    this.launcher = null;
    this.bubblePopup = null;
    this.chatWindow = null;
    this.messageList = null;
    this.input = null;
    this.button = null;
    this.closeButton = null;

    // ── Polling (human-agent handoff) ─────────────────────────────────────
    // Agent-API mode: polling URL is derived automatically
    // Legacy mode: pollingUrl can be passed explicitly
    this.pollingUrl = this._agentMode
      ? `${this.apiBaseUrl}/v1/chat/${this.agentId}/messages`
      : (options.pollingUrl || null);
    this.pollingEnabled = !!(this.pollingUrl || (options.pollingUrl));
    this.pollingInterval = options.pollingInterval || 3000;
    this._pollingTimer = null;
    this._lastMessageTimestamp = null;

    // ── State ─────────────────────────────────────────────────────────────
    // Persist sessionId in localStorage so conversations survive page reloads.
    // Each agent gets its own storage key to avoid cross-agent collisions.
    const _sessionStorageKey = this._agentMode
      ? `pindai-session-id-${this.agentId}`
      : null;
    const _restoredSession = _sessionStorageKey
      ? (() => { try { return localStorage.getItem(_sessionStorageKey); } catch (_) { return null; } })()
      : null;
    this.sessionId = _restoredSession || `web-session-${Date.now()}-${Math.random()}`;
    if (_sessionStorageKey && !_restoredSession) {
      try { localStorage.setItem(_sessionStorageKey, this.sessionId); } catch (_) {}
    }

    // ── Session expiry ────────────────────────────────────────────────────
    // After sessionExpiryHours of inactivity, automatically start a fresh session.
    // Stored as a unix timestamp alongside the sessionId.
    this.sessionExpiryHours = typeof options.sessionExpiryHours === 'number'
      ? options.sessionExpiryHours
      : 24;
    const _sessionAgeKey = _sessionStorageKey
      ? `pindai-session-age-${this.agentId}`
      : null;
    if (_sessionAgeKey && _restoredSession) {
      try {
        const savedAt = localStorage.getItem(_sessionAgeKey);
        if (savedAt) {
          const ageHours = (Date.now() - Number(savedAt)) / 3600000;
          if (ageHours > this.sessionExpiryHours) {
            // Session expired — discard and start fresh
            localStorage.removeItem(_sessionStorageKey);
            localStorage.removeItem(this.historyKey);
            localStorage.removeItem(this.stateKey);
            this.sessionId = `web-session-${Date.now()}-${Math.random()}`;
            localStorage.setItem(_sessionStorageKey, this.sessionId);
            localStorage.setItem(_sessionAgeKey, String(Date.now()));
          }
        } else {
          // Migration: stamp existing sessions without an age
          localStorage.setItem(_sessionAgeKey, String(Date.now()));
        }
      } catch (_) {}
    } else if (_sessionAgeKey && !_restoredSession) {
      try { localStorage.setItem(_sessionAgeKey, String(Date.now())); } catch (_) {}
    }

    this.isLoading = false;
    this.isOpen = false;
    this.isOnline = navigator.onLine;

    // ── Initialize ────────────────────────────────────────────────────────
    this.loadState();
    this._setupOfflineDetection();
    this._applyTheme();

    // Fetch latest appearance config from the API so dashboard changes take
    // effect immediately without re-pasting the embed snippet.
    // Falls back silently to the local init options if the request fails.
    if (this._agentMode) {
      this._fetchRemoteConfig().then(() => {
        this._applyTheme(); // re-apply theme in case it was updated by remote config
        if (this.mode === 'fullscreen') {
          this._initChatWindow();
        } else {
          this._initLauncher();
        }
      });
    } else {
      if (this.mode === 'fullscreen') {
        this._initChatWindow();
      } else {
        this._initLauncher();
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Remote config (Agent-API mode)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch the agent's current appearance + behavior config from the public
   * /v1/chat/{agentId}/config endpoint. Merges the remote values into local
   * widget properties. Falls back silently if the request fails so the widget
   * always loads with at minimum the local init options.
   */
  async _fetchRemoteConfig() {
    try {
      const res = await fetch(
        `${this.apiBaseUrl}/v1/chat/${this.agentId}/config`,
        { headers: { 'Accept': 'application/json' } }
      );
      if (!res.ok) return;
      const cfg = await res.json();

      // Appearance
      if (cfg.title)             this.title = cfg.title;
      if (cfg.initial_message)   this.initialMessage = cfg.initial_message;
      if (cfg.locale)            this.locale = cfg.locale;
      if (cfg.theme)             this.theme = cfg.theme;
      if (cfg.button_alignment)  this.buttonAlignment = cfg.button_alignment;
      if (cfg.avatar_url)        this.avatarUrl = cfg.avatar_url;
      if (cfg.launcher_icon_url) this.launcherIconUrl = cfg.launcher_icon_url;
      if (cfg.bubble_text != null) {
        this.bubbleText = cfg.bubble_text;
        // Derive bubbleMessages from bubble_text when no explicit bubble_messages array
        if (!Array.isArray(cfg.bubble_messages) || cfg.bubble_messages.length === 0) {
          this.bubbleMessages = cfg.bubble_text ? [cfg.bubble_text] : null;
        }
      }
      if (cfg.custom_footer != null) this.customFooter = cfg.custom_footer;

      // Accent color drives 3 properties
      if (cfg.accent_color) {
        this.accentColor = cfg.accent_color;
        this.launcherColor = cfg.accent_color;
        this.sendButtonColor = cfg.accent_color;
      }

      // Branding
      if (cfg.show_branding != null)          this.showBranding = cfg.show_branding;
      if (cfg.show_action_indicators != null) this.showActionIndicators = cfg.show_action_indicators;

      // File upload
      if (cfg.enable_file_upload != null) this.enableFileUpload = cfg.enable_file_upload;
      if (cfg.max_file_size_mb != null)   this.maxFileSize = cfg.max_file_size_mb * 1024 * 1024;
      if (cfg.max_files != null)          this.maxFiles = cfg.max_files;
      if (Array.isArray(cfg.allowed_file_types) && cfg.allowed_file_types.length > 0) {
        this.allowedFileTypes = cfg.allowed_file_types;
      }

      // Streaming
      if (cfg.enable_streaming != null) this.enableStreaming = this._agentMode && cfg.enable_streaming;

      // Quick replies
      if (cfg.show_quick_replies != null) this.showQuickReplies = cfg.show_quick_replies;
      if (Array.isArray(cfg.quick_replies) && cfg.quick_replies.length > 0) {
        this.quickReplies = cfg.quick_replies;
      }

      // Bubble messages
      if (Array.isArray(cfg.bubble_messages) && cfg.bubble_messages.length > 0) {
        this.bubbleMessages = cfg.bubble_messages;
      }
      if (cfg.bubble_delay_ms != null)   this.bubbleDelay = cfg.bubble_delay_ms;
      if (cfg.bubble_interval_ms != null) this.bubbleInterval = cfg.bubble_interval_ms;
      if (cfg.show_bubble_once != null && !this._showBubbleOnceExplicit) this.showBubbleOnce = cfg.show_bubble_once;

      // Lead capture
      if (cfg.lead_collection_enabled != null) this.leadCollectionEnabled = cfg.lead_collection_enabled;
      if (cfg.lead_form_trigger)               this.leadFormTrigger = cfg.lead_form_trigger;
      if (cfg.lead_form_title != null)         this.leadFormTitle = cfg.lead_form_title;
      if (Array.isArray(cfg.lead_form_fields) && cfg.lead_form_fields.length > 0) {
        this.leadFormFields = cfg.lead_form_fields;
      }
      if (cfg.lead_allow_skip != null)           this.leadAllowSkip = cfg.lead_allow_skip;
      if (cfg.lead_submit_button_text != null)   this.leadSubmitButtonText = cfg.lead_submit_button_text;
    } catch (_) {
      // Remote config is optional — widget falls back to local init options
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Auth helpers (Agent-API mode)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Convert hex string to Uint8Array
   */
  _hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return bytes;
  }

  /**
   * Generate HMAC-SHA256 embed token using native crypto.subtle
   * token = HMAC-SHA256(embedSecret, "{agentId}:{sessionId}")
   */
  async _generateEmbedToken() {
    if (this._embedToken) return this._embedToken;

    const key = await crypto.subtle.importKey(
      'raw',
      this._hexToBytes(this.embedSecret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const data = new TextEncoder().encode(`${this.agentId}:${this.sessionId}`);
    const sig = await crypto.subtle.sign('HMAC', key, data);
    this._embedToken = Array.from(new Uint8Array(sig))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return this._embedToken;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Theme
  // ─────────────────────────────────────────────────────────────────────────

  _applyTheme() {
    if (this.theme === 'dark') {
      document.documentElement.setAttribute('data-pindai-theme', 'dark');
    } else if (this.theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
      const update = (e) => {
        document.documentElement.setAttribute(
          'data-pindai-theme',
          e.matches ? 'dark' : 'light'
        );
      };
      update(prefersDark);
      prefersDark.addEventListener('change', update);
    }
    // 'light' is the default — no attribute needed
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Launcher
  // ─────────────────────────────────────────────────────────────────────────

  _initLauncher() {
    const wrapper = document.createElement('div');
    wrapper.className = 'n8n-chat-launcher-wrapper';
    if (this.buttonAlignment === 'bottom-left') {
      wrapper.classList.add('n8n-chat-launcher-wrapper--left');
    }

    // Speech bubble popup
    if (this.bubbleMessages && !(this.showBubbleOnce && this._bubbleDismissed)) {
      this.bubblePopup = document.createElement('div');
      this.bubblePopup.className = 'n8n-chat-bubble-popup';
      // Start hidden; shown after bubbleDelay
      this.bubblePopup.style.display = 'none';

      const textSpan = document.createElement('span');
      textSpan.className = 'n8n-chat-bubble-popup-text';
      textSpan.textContent = this.bubbleMessages[0];

      const dismissBtn = document.createElement('button');
      dismissBtn.className = 'n8n-chat-bubble-popup-dismiss';
      dismissBtn.setAttribute('aria-label', this.i18n.t('bubbleDismiss'));
      dismissBtn.textContent = '\u00D7';
      dismissBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._dismissBubble();
      });

      const arrow = document.createElement('div');
      arrow.className = 'n8n-chat-bubble-popup-arrow';

      this.bubblePopup.appendChild(textSpan);
      this.bubblePopup.appendChild(dismissBtn);
      this.bubblePopup.appendChild(arrow);
      wrapper.appendChild(this.bubblePopup);

      // Store reference to text span for rotation
      this._bubbleTextSpan = textSpan;

      // Delay before first appearance
      this._bubbleDelayTimer = setTimeout(() => {
        if (!this._bubbleDismissed && this.bubblePopup) {
          this.bubblePopup.style.display = 'flex';
          // Start rotating if multiple messages
          if (this.bubbleMessages.length > 1) {
            this._startBubbleRotation();
          }
        }
      }, this.bubbleDelay);
    }

    this.launcher = document.createElement('div');
    this.launcher.className = 'n8n-chat-launcher';
    this.launcher.style.backgroundColor = this.launcherColor;
    this.launcher.setAttribute('role', 'button');
    this.launcher.setAttribute('aria-label', this.i18n.t('ariaOpenChat'));
    this.launcher.setAttribute('tabindex', '0');

    const launcherImg = document.createElement('img');
    launcherImg.src = this.launcherIconUrl;
    launcherImg.alt = '';
    launcherImg.onerror = () => {
      // Fall back to the built-in SVG data URI
      launcherImg.onerror = null;
      launcherImg.src = this._getDefaultLauncherIcon();
    };

    const badge = document.createElement('span');
    badge.className = 'n8n-chat-unread-badge';
    badge.style.display = 'none';
    badge.textContent = '0';

    this.launcher.appendChild(launcherImg);
    this.launcher.appendChild(badge);

    wrapper.appendChild(this.launcher);
    document.body.appendChild(wrapper);

    this.launcher.addEventListener('click', () => this.toggleChatWindow());
    this.launcher.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleChatWindow();
      }
    });
  }

  _dismissBubble() {
    // Stop pending timers
    if (this._bubbleDelayTimer) {
      clearTimeout(this._bubbleDelayTimer);
      this._bubbleDelayTimer = null;
    }
    this._stopBubbleRotation();

    if (this.bubblePopup) {
      this.bubblePopup.style.display = 'none';
    }
    this._bubbleDismissed = true;
    this.saveState(); // Persist dismiss so it doesn't re-appear (when showBubbleOnce: true)
  }

  /**
   * Re-show the bubble after the chat window closes.
   * Only called when showBubbleOnce is false — the bubble stays alive indefinitely.
   */
  _reshowBubble() {
    if (!this.bubblePopup || !this.bubbleMessages) return;
    this._bubbleDismissed = false;
    this._bubbleMsgIndex = 0;
    if (this._bubbleTextSpan) {
      this._bubbleTextSpan.textContent = this.bubbleMessages[0];
    }
    this._bubbleDelayTimer = setTimeout(() => {
      if (!this._bubbleDismissed && this.bubblePopup) {
        this.bubblePopup.style.display = 'flex';
        if (this.bubbleMessages.length > 1) {
          this._startBubbleRotation();
        }
      }
    }, this.bubbleDelay);
  }

  _startBubbleRotation() {
    this._stopBubbleRotation();
    this._bubbleRotateTimer = setInterval(() => {
      if (!this.bubblePopup || this._bubbleDismissed) {
        this._stopBubbleRotation();
        return;
      }
      this._bubbleMsgIndex = (this._bubbleMsgIndex + 1) % this.bubbleMessages.length;
      if (this._bubbleTextSpan) {
        // Animate out → swap text → animate in
        this._bubbleTextSpan.classList.add('n8n-chat-bubble-popup-text--exit');
        setTimeout(() => {
          if (this._bubbleTextSpan) {
            this._bubbleTextSpan.textContent = this.bubbleMessages[this._bubbleMsgIndex];
            this._bubbleTextSpan.classList.remove('n8n-chat-bubble-popup-text--exit');
            this._bubbleTextSpan.classList.add('n8n-chat-bubble-popup-text--enter');
            setTimeout(() => {
              this._bubbleTextSpan && this._bubbleTextSpan.classList.remove('n8n-chat-bubble-popup-text--enter');
            }, 300);
          }
        }, 250);
      }
    }, this.bubbleInterval);
  }

  _stopBubbleRotation() {
    if (this._bubbleRotateTimer) {
      clearInterval(this._bubbleRotateTimer);
      this._bubbleRotateTimer = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Chat Window
  // ─────────────────────────────────────────────────────────────────────────

  _initChatWindow() {
    this.container = document.createElement('div');
    this.container.className = `n8n-chat-widget ${this.mode === 'fullscreen' ? 'n8n-chat-widget--fullscreen' : ''} ${this.buttonAlignment === 'bottom-left' ? 'n8n-chat-widget--left' : ''}`.trimEnd();
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-label', this.title);
    // Drive widget accent-dependent CSS from the configured accentColor.
    this.container.style.setProperty('--pindai-primary', this.accentColor);
    this.container.style.setProperty('--pindai-primary-dark', this.accentColor);

    // Footer HTML
    const footerHtml = this._buildFooterHtml();

    this.container.innerHTML = `
      <div class="n8n-chat-header">
        <div class="n8n-chat-header-content">
          <span class="n8n-chat-title">${this._escapeHtml(this.title)}</span>
        </div>
        <button class="n8n-chat-new-btn" aria-label="${this.i18n.t('ariaNewConversation')}" title="${this.i18n.t('newConversation')}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button class="n8n-chat-close-btn" aria-label="${this.i18n.t('ariaCloseChat')}">&times;</button>
      </div>
      <div class="n8n-chat-messages" role="log" aria-live="polite" aria-atomic="false"></div>
      ${footerHtml}
      <div class="n8n-chat-input-area">
        ${this.enableFileUpload ? `
          <label class="n8n-chat-file-upload-btn" aria-label="${this.i18n.t('ariaUploadFile')}">
            <input type="file" multiple accept="${this.allowedFileTypes.join(',')}" hidden>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </label>
        ` : ''}
        <input type="text" placeholder="${this.i18n.t('placeholder')}" aria-label="${this.i18n.t('ariaMessageInput')}" />
        <button class="n8n-chat-send-btn" style="background-color: ${this.sendButtonColor}" aria-label="${this.i18n.t('ariaSendMessage')}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
      ${this.enableFileUpload ? '<div class="n8n-chat-file-preview" style="display: none;"></div>' : ''}
    `;

    // Inject header image as DOM element (allows onerror fallback)
    this._injectHeaderImage();

    if (this.mode === 'widget') {
      document.body.appendChild(this.container);
    } else {
      document.body.innerHTML = '';
      document.body.appendChild(this.container);
      document.body.style.margin = '0';
    }

    // DOM references
    this.messageList = this.container.querySelector('.n8n-chat-messages');
    this.input = this.container.querySelector('input[type="text"]');
    this.button = this.container.querySelector('.n8n-chat-send-btn');
    this.closeButton = this.container.querySelector('.n8n-chat-close-btn');
    this.newButton = this.container.querySelector('.n8n-chat-new-btn');

    // Event listeners
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.sendMessage();
    });

    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.sendMessage();
      }
    });

    if (this.mode === 'fullscreen') {
      this.closeButton.style.display = 'none';
    } else {
      this.closeButton.addEventListener('click', () => this.toggleChatWindow());
    }

    if (this.newButton) {
      this.newButton.addEventListener('click', () => this._resetSession());
    }

    if (this.enableFileUpload) {
      const fileInput = this.container.querySelector('input[type="file"]');
      fileInput.addEventListener('change', (e) => this._handleFileSelect(e));
    }

    this._setupKeyboardNavigation();
    this.loadHistory();

    if (this.messageList.children.length === 0) {
      this.addMessage(this.initialMessage, 'ai');
      // In-chat lead form: inject card after welcome message
      if (this._shouldShowLeadForm() && this.leadFormTrigger === 'in_chat') {
        setTimeout(() => this._renderLeadForm('card'), 400);
      }
    }
  }

  /**
   * Inject the header avatar/logo as a real DOM element so we can attach
   * an onerror handler. Falls back gracefully when the image cannot be loaded:
   *   - avatarUrl fails  → image is hidden (no broken icon in header)
   *   - logoUrl fails    → image is hidden (clean header, title still shows)
   */
  _injectHeaderImage() {
    const headerContent = this.container.querySelector('.n8n-chat-header-content');
    if (!headerContent) return;

    const titleSpan = headerContent.querySelector('.n8n-chat-title');

    if (this.avatarUrl) {
      const img = document.createElement('img');
      img.className = 'n8n-chat-header-avatar';
      img.alt = '';
      img.src = this.avatarUrl;
      img.onerror = () => {
        img.onerror = null;
        img.remove(); // Hide broken avatar — title still visible
      };
      headerContent.insertBefore(img, titleSpan);
    } else if (this.showLogo) {
      const img = document.createElement('img');
      img.className = 'n8n-chat-logo';
      img.alt = '';
      img.src = this.logoUrl;
      img.onerror = () => {
        img.onerror = null;
        img.remove(); // Hide broken logo — title still visible
      };
      headerContent.insertBefore(img, titleSpan);
    }
  }

  /**
   * Build footer HTML based on showBranding + customFooter options.
   * Mirrors the dashboard "Branding" tab behaviour:
   *   - showBranding: true  + no customFooter  → "Powered by Pindai.ai"
   *   - showBranding: true  + customFooter set  → "Powered by Pindai.ai | <customFooter>"
   *   - showBranding: false + customFooter set  → only "<customFooter>"
   *   - showBranding: false + no customFooter   → no footer
   */
  _buildFooterHtml() {
    const parts = [];

    if (this.showBranding) {
      parts.push(`<span>${this.i18n.t('poweredBy')} <a href="https://pindai.ai" target="_blank" rel="noopener noreferrer">Pindai.ai</a></span>`);
    }

    if (this.customFooter) {
      parts.push(`<span>${this._renderSimpleMarkdown(this.customFooter)}</span>`);
    }

    if (parts.length === 0) return '';

    return `<div class="n8n-chat-watermark">${parts.join(' <span class="n8n-chat-footer-sep">|</span> ')}</div>`;
  }

  /**
   * Minimal safe markdown renderer — only handles links: [text](url)
   * No eval, no innerHTML from untrusted HTML — URLs are validated.
   */
  _renderSimpleMarkdown(text) {
    const escaped = this._escapeHtml(text);
    // Convert [text](url) — only allow http/https URLs
    return escaped.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
  }

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Safe markdown renderer for AI messages.
   * Supports: bold, italic, inline code, fenced code blocks, headings,
   * unordered/ordered lists, links. Always escapes HTML first to prevent XSS.
   */
  _renderMarkdown(text) {
    // 1. Extract fenced code blocks before any other processing to avoid
    //    double-escaping or transforming their contents.
    const codeBlocks = [];
    let html = String(text).replace(/```([\s\S]*?)```/g, (_, code) => {
      const idx = codeBlocks.length;
      codeBlocks.push(`<pre><code>${this._escapeHtml(code.replace(/^\n/, '').replace(/\n$/, ''))}</code></pre>`);
      return `\x00CODE${idx}\x00`;
    });

    // 2. Escape remaining HTML
    html = this._escapeHtml(html);

    // 3. Inline code (backtick) — must come before bold/italic
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // 4. Bold: **text** or __text__
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // 5. Italic: *text* or _text_ (single, not double)
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    // 6. Headings (# ## ###)
    html = html.replace(/^### (.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^## (.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>');

    // 7. Unordered lists — group consecutive "- " or "* " lines
    html = html.replace(/^(?:[*-] .+\n?)+/gm, (block) => {
      const items = block.trim().split('\n').map(line =>
        `<li>${line.replace(/^[*-] /, '')}</li>`
      ).join('');
      return `<ul>${items}</ul>`;
    });

    // 8. Ordered lists — group consecutive "N. " lines
    html = html.replace(/^(?:\d+\. .+\n?)+/gm, (block) => {
      const items = block.trim().split('\n').map(line =>
        `<li>${line.replace(/^\d+\. /, '')}</li>`
      ).join('');
      return `<ol>${items}</ol>`;
    });

    // 9. Links [text](url) — http/https only
    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // 10. Convert newlines to <br> (outside of block elements)
    html = html.replace(/\n/g, '<br>');

    // 11. Restore fenced code blocks
    html = html.replace(/\x00CODE(\d+)\x00/g, (_, idx) => codeBlocks[parseInt(idx)]);

    return html;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Toggle open/close
  // ─────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────
  // Lead capture
  // ─────────────────────────────────────────────────────────────────────────

  /** Returns true if the lead form should be shown for this session. */
  _shouldShowLeadForm() {
    return (
      this._agentMode &&
      this.leadCollectionEnabled &&
      !this._leadDone &&
      this.leadFormTrigger !== 'conversational'
    );
  }

  /** Persist that the lead form was handled (submitted or skipped). */
  _markLeadDone() {
    this._leadDone = true;
    if (this._leadStateKey) {
      try { sessionStorage.setItem(this._leadStateKey, '1'); } catch (_) {}
    }
  }

  /**
   * Build and inject the lead capture form.
   * mode: 'overlay' (pre-chat — covers the widget before messages)
   *       'card'    (in-chat — injected as first message card)
   */
  _renderLeadForm(mode) {
    const title = this.leadFormTitle
      || 'Before we start, let us know how to reach you.';
    const fields = Array.isArray(this.leadFormFields)
      ? this.leadFormFields
      : ['name', 'email', 'phone'];
    const submitText = this.leadSubmitButtonText || 'Start Chat';
    const skipText = 'Skip';

    const fieldHtml = fields.map(f => {
      if (f === 'name') return `
        <div class="pindai-lead-field">
          <label class="pindai-lead-label">Name</label>
          <input class="pindai-lead-input" type="text" name="lead_name" placeholder="Your name" autocomplete="name">
        </div>`;
      if (f === 'email') return `
        <div class="pindai-lead-field">
          <label class="pindai-lead-label">Email</label>
          <input class="pindai-lead-input" type="email" name="lead_email" placeholder="name@example.com" autocomplete="email">
        </div>`;
      if (f === 'phone') return `
        <div class="pindai-lead-field">
          <label class="pindai-lead-label">Phone</label>
          <input class="pindai-lead-input" type="tel" name="lead_phone" placeholder="+1 555 000 0000" autocomplete="tel">
        </div>`;
      return '';
    }).join('');

    const skipHtml = this.leadAllowSkip
      ? `<button class="pindai-lead-skip" type="button">${skipText}</button>`
      : '';

    if (mode === 'overlay') {
      const overlay = document.createElement('div');
      overlay.className = 'pindai-lead-overlay';
      overlay.innerHTML = `
        <div class="pindai-lead-card">
          <p class="pindai-lead-title">${this._renderSimpleMarkdown(title)}</p>
          <form class="pindai-lead-form" novalidate>
            ${fieldHtml}
            <div class="pindai-lead-actions">
              <button class="pindai-lead-submit" type="submit">${this._escapeHtml(submitText)}</button>
              ${skipHtml}
            </div>
          </form>
          <p class="pindai-lead-error" style="display:none"></p>
        </div>
      `;
      this.container.appendChild(overlay);
      this._bindLeadFormEvents(overlay);
    } else {
      // in-chat card injected as a message-row style element
      const card = document.createElement('div');
      card.className = 'pindai-lead-chat-card';
      card.innerHTML = `
        <p class="pindai-lead-title">${this._renderSimpleMarkdown(title)}</p>
        <form class="pindai-lead-form" novalidate>
          ${fieldHtml}
          <div class="pindai-lead-actions">
            <button class="pindai-lead-submit" type="submit">${this._escapeHtml(submitText)}</button>
            ${skipHtml}
          </div>
        </form>
        <p class="pindai-lead-error" style="display:none"></p>
      `;
      this.messageList.appendChild(card);
      this.messageList.scrollTop = this.messageList.scrollHeight;
      this._bindLeadFormEvents(card);
    }
  }

  _bindLeadFormEvents(container) {
    const form = container.querySelector('.pindai-lead-form');
    const errorEl = container.querySelector('.pindai-lead-error');
    const skipBtn = container.querySelector('.pindai-lead-skip');

    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this._markLeadDone();
        container.remove();
      });
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const name  = (form.querySelector('[name="lead_name"]')?.value  || '').trim();
      const email = (form.querySelector('[name="lead_email"]')?.value || '').trim();
      const phone = (form.querySelector('[name="lead_phone"]')?.value || '').trim();

      if (!name && !email && !phone) {
        errorEl.textContent = 'Please fill in at least one field.';
        errorEl.style.display = 'block';
        return;
      }

      const submitBtn = form.querySelector('.pindai-lead-submit');
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
      errorEl.style.display = 'none';

      try {
        await this._submitLead({ name, email, phone });
        this._markLeadDone();
        container.remove();
      } catch (_) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = this.leadSubmitButtonText || 'Start Chat';
      }
    });
  }

  async _submitLead({ name, email, phone }) {
    const token = await this._generateEmbedToken();
    const fd = new FormData();
    fd.append('embedToken', token);
    fd.append('sessionId', this.sessionId);
    if (name)  fd.append('name', name);
    if (email) fd.append('email', email);
    if (phone) fd.append('phone', phone);

    const res = await fetch(
      `${this.apiBaseUrl}/v1/chat/${this.agentId}/lead`,
      { method: 'POST', body: fd }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }

  toggleChatWindow() {
    if (!this.isOpen) {
      if (!this.container) {
        this._initChatWindow();
      }
      // Pre-chat lead form: show overlay before revealing messages
      if (this._shouldShowLeadForm() && this.leadFormTrigger === 'pre_chat') {
        setTimeout(() => {
          this.container.classList.add('n8n-chat-widget--open');
          if (this.launcher) {
            this.launcher.classList.add('n8n-chat-launcher--hidden');
          }
          this._dismissBubble();
          this._renderLeadForm('overlay');
        }, 10);
        this.isOpen = true;
        this.saveState();
        return;
      }
      setTimeout(() => {
        this.container.classList.add('n8n-chat-widget--open');
        if (this.launcher) {
          this.launcher.classList.add('n8n-chat-launcher--hidden');
        }
        this._dismissBubble();
        this.input.focus();
        this._clearUnreadCount();
      }, 10);
    } else {
      this.container.classList.remove('n8n-chat-widget--open');
      if (this.launcher) {
        this.launcher.classList.remove('n8n-chat-launcher--hidden');
      }
      // Re-show bubble after chat closes when showBubbleOnce is disabled
      if (!this.showBubbleOnce) {
        this._reshowBubble();
      }
    }
    this.isOpen = !this.isOpen;
    this.saveState();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Default launcher icon
  // ─────────────────────────────────────────────────────────────────────────

  _getDefaultLauncherIcon() {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Timestamp formatting
  // ─────────────────────────────────────────────────────────────────────────

  _formatTimestamp(date) {
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return this.i18n.t('justNow');
    if (diff < 3600000) {
      return this.i18n.t('minutesAgo', { minutes: Math.floor(diff / 60000) });
    }
    return date.toLocaleTimeString(this.locale === 'id' ? 'id-ID' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Messages
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build a small avatar element (img or fallback circle) for AI message rows.
   * Returns null when avatarUrl is not set, so callers skip the avatar column.
   */
  _createAvatarEl() {
    if (!this.avatarUrl) return null;
    const img = document.createElement('img');
    img.className = 'n8n-chat-msg-avatar';
    img.src = this.avatarUrl;
    img.alt = '';
    img.onerror = () => { img.onerror = null; img.remove(); };
    return img;
  }

  /**
   * Append an element to the message list, wrapping AI bubbles in a row
   * that includes the avatar thumbnail on the left.
   */
  _appendToMessageList(el, sender) {
    if (sender === 'ai' || sender === 'human_agent') {
      const avatar = this._createAvatarEl();
      if (avatar) {
        const row = document.createElement('div');
        row.className = 'n8n-chat-msg-row';
        row.appendChild(avatar);
        row.appendChild(el);
        this.messageList.appendChild(row);
        this.messageList.scrollTop = this.messageList.scrollHeight;
        return row;
      }
    }
    this.messageList.appendChild(el);
    this.messageList.scrollTop = this.messageList.scrollHeight;
    return el;
  }

  addMessage(text, sender, timestamp = new Date()) {
    const bubble = document.createElement('div');
    bubble.className = `n8n-chat-bubble n8n-chat-${sender}-message`;

    const textNode = document.createElement('div');
    textNode.className = 'n8n-chat-message-text';
    // Render markdown for AI messages; keep user messages as plain text (XSS safety)
    if (sender === 'ai') {
      textNode.innerHTML = this._renderMarkdown(text);
    } else {
      textNode.textContent = text;
    }

    const timeNode = document.createElement('div');
    timeNode.className = 'n8n-chat-message-timestamp';
    timeNode.textContent = this._formatTimestamp(timestamp);
    timeNode.setAttribute('data-timestamp', timestamp.toISOString());

    bubble.appendChild(textNode);
    bubble.appendChild(timeNode);
    this._appendToMessageList(bubble, sender);

    this.saveToHistory(text, sender, timestamp);

    if (!this.isOpen && sender === 'ai') {
      this._incrementUnread();
    }
  }

  /**
   * Create a streaming message bubble (text filled incrementally via SSE).
   * Returns the text node for direct DOM updates.
   */
  _createStreamingBubble() {
    const bubble = document.createElement('div');
    bubble.className = 'n8n-chat-bubble n8n-chat-ai-message n8n-chat-bubble--streaming';

    const textNode = document.createElement('div');
    textNode.className = 'n8n-chat-message-text';
    textNode.textContent = '';

    const cursor = document.createElement('span');
    cursor.className = 'n8n-chat-stream-cursor';

    const timeNode = document.createElement('div');
    timeNode.className = 'n8n-chat-message-timestamp';
    timeNode.textContent = this._formatTimestamp(new Date());

    bubble.appendChild(textNode);
    bubble.appendChild(cursor);
    bubble.appendChild(timeNode);
    this._appendToMessageList(bubble, 'ai');

    return { bubble, textNode, cursor, timeNode };
  }

  addMessageWithoutSaving(text, sender, timestamp) {
    const bubble = document.createElement('div');
    bubble.className = `n8n-chat-bubble n8n-chat-${sender}-message`;

    const textNode = document.createElement('div');
    textNode.className = 'n8n-chat-message-text';
    if (sender === 'ai') {
      textNode.innerHTML = this._renderMarkdown(text);
    } else {
      textNode.textContent = text;
    }

    const timeNode = document.createElement('div');
    timeNode.className = 'n8n-chat-message-timestamp';
    timeNode.textContent = this._formatTimestamp(timestamp);
    timeNode.setAttribute('data-timestamp', timestamp.toISOString());

    bubble.appendChild(textNode);
    bubble.appendChild(timeNode);
    this._appendToMessageList(bubble, sender);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Typing indicator
  // ─────────────────────────────────────────────────────────────────────────

  _showTypingIndicator(show) {
    // The indicator may be inside a .n8n-chat-msg-row wrapper — check both
    const existing = this.messageList.querySelector('.n8n-chat-typing-indicator');
    if (show) {
      if (!existing) {
        const indicator = document.createElement('div');
        indicator.className = 'n8n-chat-bubble n8n-chat-ai-message n8n-chat-typing-indicator';

        const dotsHtml = '<div class="n8n-chat-typing-dots"><span></span><span></span><span></span></div>';
        if (this.showActionIndicators) {
          indicator.innerHTML = `
            ${dotsHtml}
            <span class="n8n-chat-typing-label" data-action-label></span>
          `;
        } else {
          indicator.innerHTML = dotsHtml;
        }
        indicator.setAttribute('aria-label', this.i18n.t('typingIndicator'));
        this._appendToMessageList(indicator, 'ai');

        // Start cycling action labels when showActionIndicators is on
        if (this.showActionIndicators) {
          this._startActionLabelCycle(indicator.querySelector('[data-action-label]'));
        }
      }
    } else {
      if (existing) {
        this._stopActionLabelCycle();
        // Remove the wrapper row if it exists, else remove the bubble itself
        const row = existing.closest('.n8n-chat-msg-row');
        (row || existing).remove();
      }
    }
  }

  /**
   * Cycle through action indicator phrases while the AI is processing.
   * Inspired by Claude Code's "Thinking… Planning… Working on it…" pattern.
   */
  _startActionLabelCycle(labelEl) {
    if (!labelEl) return;
    const phases = [
      this.i18n.t('thinkingIndicator'),   // "Thinking..."
      this.i18n.t('actionIndicator'),      // "Performing action..."
      'Working on it\u2026',
      'Almost there\u2026',
    ];
    let idx = 0;
    labelEl.textContent = phases[0];
    this._actionLabelTimer = setInterval(() => {
      idx = (idx + 1) % phases.length;
      labelEl.classList.add('n8n-chat-typing-label--exit');
      setTimeout(() => {
        if (labelEl) {
          labelEl.textContent = phases[idx];
          labelEl.classList.remove('n8n-chat-typing-label--exit');
          labelEl.classList.add('n8n-chat-typing-label--enter');
          setTimeout(() => labelEl && labelEl.classList.remove('n8n-chat-typing-label--enter'), 300);
        }
      }, 200);
    }, 2000);
  }

  _stopActionLabelCycle() {
    if (this._actionLabelTimer) {
      clearInterval(this._actionLabelTimer);
      this._actionLabelTimer = null;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // File upload
  // ─────────────────────────────────────────────────────────────────────────

  _handleFileSelect(event) {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      if (!this.allowedFileTypes.includes(file.type)) {
        this.addMessage(this.i18n.t('fileTypeNotSupported', { filename: file.name }), 'ai');
        return;
      }
      if (file.size > this.maxFileSize) {
        const maxSizeMB = this.maxFileSize / 1024 / 1024;
        this.addMessage(this.i18n.t('fileTooLarge', { filename: file.name, maxSize: maxSizeMB }), 'ai');
        return;
      }
      if (this.uploadedFiles.length >= this.maxFiles) {
        this.addMessage(this.i18n.t('maxFilesExceeded', { maxFiles: this.maxFiles }), 'ai');
        return;
      }
      this.uploadedFiles.push(file);
      this._renderFilePreview(file);
    });
    event.target.value = '';
  }

  _renderFilePreview(file) {
    const preview = this.container.querySelector('.n8n-chat-file-preview');
    if (!preview) return;
    preview.style.display = 'flex';

    const fileItem = document.createElement('div');
    fileItem.className = 'n8n-chat-file-item';
    fileItem.innerHTML = `
      <span class="n8n-chat-file-name">${this._escapeHtml(file.name)}</span>
      <button class="n8n-chat-file-remove" aria-label="${this.i18n.t('ariaRemoveFile')}">&times;</button>
    `;
    fileItem.querySelector('.n8n-chat-file-remove').addEventListener('click', () => {
      this.uploadedFiles = this.uploadedFiles.filter(f => f.name !== file.name);
      fileItem.remove();
      if (this.uploadedFiles.length === 0) preview.style.display = 'none';
    });
    preview.appendChild(fileItem);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Send message — entry point
  // ─────────────────────────────────────────────────────────────────────────

  async sendMessage() {
    const messageText = this.input.value.trim();
    if ((!messageText && this.uploadedFiles.length === 0) || this.isLoading) return;

    try {
      this._checkRateLimit();
    } catch (error) {
      this.addMessage(error.message, 'ai');
      return;
    }

    if (!this.isOnline) {
      this.addMessage(this.i18n.t('connectionLost'), 'ai');
      return;
    }

    this.isLoading = true;
    this.button.disabled = true;
    this.input.disabled = true;

    if (messageText) {
      this.addMessage(messageText, 'user');
    }

    this.input.value = '';
    this._showTypingIndicator(true);

    try {
      // Use SSE streaming if agent-mode, streaming enabled, and no files attached
      if (this._agentMode && this.enableStreaming && this.uploadedFiles.length === 0) {
        await this._sendWithStreaming(messageText);
      } else {
        const response = await this._sendMessageWithRetry(messageText, this.uploadedFiles);
        this._showTypingIndicator(false);
        this.addMessage(response, 'ai');

        if (this.showQuickReplies && this.quickReplies.length > 0) {
          this._renderQuickReplies();
        }
      }
    } catch (error) {
      this._showTypingIndicator(false);
      this.addMessage(this._getErrorMessage(error), 'ai');
    } finally {
      this.isLoading = false;
      this.button.disabled = false;
      this.input.disabled = false;
      this.input.focus();

      if (this.uploadedFiles.length > 0) {
        this.uploadedFiles = [];
        const preview = this.container.querySelector('.n8n-chat-file-preview');
        if (preview) {
          preview.innerHTML = '';
          preview.style.display = 'none';
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SSE streaming (agent-api mode, no files)
  // ─────────────────────────────────────────────────────────────────────────

  async _sendWithStreaming(messageText) {
    const token = await this._generateEmbedToken();
    const params = new URLSearchParams({
      message: messageText,
      sessionId: this.sessionId,
      embedToken: token
    });
    if (this.visitorName) params.set('visitor_name', this.visitorName);
    if (this.visitorEmail) params.set('visitor_email', this.visitorEmail);

    const url = `${this.apiBaseUrl}/v1/chat/${this.agentId}/stream?${params}`;

    return new Promise((resolve, reject) => {
      // Hide typing indicator once streaming starts
      this._showTypingIndicator(false);

      const { bubble, textNode, cursor, timeNode } = this._createStreamingBubble();
      let fullText = '';
      let finalStatus = 'active';

      const es = new EventSource(url);

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);

          if (data.delta) {
            fullText += data.delta;
            textNode.textContent = fullText;
            this.messageList.scrollTop = this.messageList.scrollHeight;
          }

          if (data.type === 'done') {
            es.close();
            cursor.remove();
            bubble.classList.remove('n8n-chat-bubble--streaming');
            timeNode.textContent = this._formatTimestamp(new Date());
            // Render final accumulated text as markdown now that streaming is complete
            textNode.innerHTML = this._renderMarkdown(fullText);
            finalStatus = data.status || 'active';
            this.saveToHistory(fullText, 'ai');

            if (finalStatus && finalStatus !== 'active') {
              this._startPolling();
              this._showHandoffBanner(true);
              this.addMessage(this.i18n.t('humanAgentJoined'), 'ai');
            }

            if (this.showQuickReplies && this.quickReplies.length > 0) {
              this._renderQuickReplies();
            }
            resolve(fullText);
          }
        } catch (_) {
          // Non-JSON event — skip
        }
      };

      es.onerror = () => {
        es.close();
        cursor.remove();
        bubble.classList.remove('n8n-chat-bubble--streaming');
        // If we already got some text, treat as partial success
        if (fullText) {
          this.saveToHistory(fullText, 'ai');
          resolve(fullText);
        } else {
          reject(new Error(this.i18n.t('streamingError')));
        }
      };
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // POST with retry (agent-api mode + legacy mode)
  // ─────────────────────────────────────────────────────────────────────────

  async _sendMessageWithRetry(messageText, files = [], retryCount = 0) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

      const formData = new FormData();
      formData.append('sessionId', this.sessionId);
      formData.append('message', messageText);

      if (this._agentMode) {
        const token = await this._generateEmbedToken();
        formData.append('embedToken', token);
        if (this.visitorName) formData.append('visitor_name', this.visitorName);
        if (this.visitorEmail) formData.append('visitor_email', this.visitorEmail);
      } else if (this.embedToken) {
        // Legacy: static embedToken passed in options
        formData.append('embedToken', this.embedToken);
      }

      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });

      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 500 && retryCount < this.maxRetries) {
          await this._delay(this.retryDelay * (retryCount + 1));
          return this._sendMessageWithRetry(messageText, files, retryCount + 1);
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Network error: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.response) {
        throw new Error(this.i18n.t('errorInvalidResponse'));
      }

      // Handle handoff status
      if (data.status && data.status !== 'active' && this.pollingUrl) {
        this._startPolling();
        this._showHandoffBanner(true);
        this.addMessage(this.i18n.t('humanAgentJoined'), 'ai');
      } else {
        this._stopPolling();
      }

      return data.response;

    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(this.i18n.t('errorTimeout'));
      }
      if ((error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))
          && retryCount < this.maxRetries) {
        await this._delay(this.retryDelay * (retryCount + 1));
        return this._sendMessageWithRetry(messageText, files, retryCount + 1);
      }
      throw error;
    }
  }

  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  _getErrorMessage(error) {
    const msg = error.message || '';
    if (msg.includes('timeout') || msg.includes('Timeout')) return this.i18n.t('errorTimeout');
    if (msg.includes('NetworkError') || msg.includes('Failed to fetch')) return this.i18n.t('errorNetwork');
    if (msg.includes('500') || msg.includes('503')) return this.i18n.t('errorServer');
    if (msg === this.i18n.t('streamingError')) return msg;
    return this.i18n.t('errorGeneric');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Rate limiting
  // ─────────────────────────────────────────────────────────────────────────

  _checkRateLimit() {
    const now = Date.now();
    this.messageTimes = this.messageTimes.filter(t => now - t < this.rateLimitWindow);

    if (this.messageTimes.length >= this.rateLimit) {
      const waitTime = Math.ceil((this.rateLimitWindow - (now - this.messageTimes[0])) / 1000);
      throw new Error(this.i18n.t('errorRateLimit', { seconds: waitTime }));
    }
    this.messageTimes.push(now);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Quick replies
  // ─────────────────────────────────────────────────────────────────────────

  _renderQuickReplies(replies = this.quickReplies) {
    if (!this.showQuickReplies || replies.length === 0) return;

    const existing = this.messageList.querySelector('.n8n-chat-quick-replies');
    if (existing) existing.remove();

    const container = document.createElement('div');
    container.className = 'n8n-chat-quick-replies';

    replies.forEach(reply => {
      const btn = document.createElement('button');
      btn.className = 'n8n-chat-quick-reply-btn';
      btn.textContent = reply;
      btn.addEventListener('click', () => {
        this.input.value = reply;
        this.sendMessage();
        container.remove();
      });
      container.appendChild(btn);
    });

    this.messageList.appendChild(container);
    this.messageList.scrollTop = this.messageList.scrollHeight;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Notification badge
  // ─────────────────────────────────────────────────────────────────────────

  _incrementUnread() {
    if (!this.isOpen) {
      this.unreadCount++;
      this._updateUnreadBadge();
    }
  }

  _updateUnreadBadge() {
    if (!this.launcher) return;
    const badge = this.launcher.querySelector('.n8n-chat-unread-badge');
    if (badge) {
      badge.textContent = this.unreadCount;
      badge.style.display = this.unreadCount > 0 ? 'flex' : 'none';
    }
  }

  _clearUnreadCount() {
    this.unreadCount = 0;
    this._updateUnreadBadge();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // History persistence
  // ─────────────────────────────────────────────────────────────────────────

  loadHistory() {
    if (!this.enableHistory) return;
    try {
      const stored = localStorage.getItem(this.historyKey);
      if (!stored) return;
      JSON.parse(stored).forEach(item => {
        this.addMessageWithoutSaving(item.text, item.sender, new Date(item.timestamp));
      });
    } catch (error) {
      console.warn('Failed to load chat history:', error);
    }
  }

  saveToHistory(text, sender, timestamp = new Date()) {
    if (!this.enableHistory) return;
    try {
      const stored = localStorage.getItem(this.historyKey);
      let history = stored ? JSON.parse(stored) : [];
      history.push({ text, sender, timestamp: timestamp.toISOString() });
      if (history.length > this.maxHistoryItems) {
        history = history.slice(-this.maxHistoryItems);
      }
      localStorage.setItem(this.historyKey, JSON.stringify(history));
    } catch (error) {
      console.warn('Failed to save chat history:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // State persistence
  // ─────────────────────────────────────────────────────────────────────────

  loadState() {
    try {
      const stored = localStorage.getItem(this.stateKey);
      if (stored) {
        const state = JSON.parse(stored);
        // Only restore the dismissed flag when showBubbleOnce is true.
        // If showBubbleOnce is false the bubble should always appear on load.
        this._bubbleDismissed = this.showBubbleOnce
          ? (state.bubbleDismissed || false)
          : false;
      }
    } catch (error) {
      console.warn('Failed to load chat state:', error);
    }
  }

  saveState() {
    try {
      localStorage.setItem(this.stateKey, JSON.stringify({
        isOpen: this.isOpen,
        bubbleDismissed: this._bubbleDismissed,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.warn('Failed to save chat state:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Offline detection
  // ─────────────────────────────────────────────────────────────────────────

  _setupOfflineDetection() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this._updateOnlineStatus();
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this._updateOnlineStatus();
    });
  }

  _updateOnlineStatus() {
    if (!this.container) return;
    const statusBar = this.container.querySelector('.n8n-chat-offline-indicator');
    if (!this.isOnline && !statusBar) {
      const indicator = document.createElement('div');
      indicator.className = 'n8n-chat-offline-indicator';
      indicator.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="1" y1="1" x2="23" y2="23"></line>
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
          <line x1="12" y1="20" x2="12.01" y2="20"></line>
        </svg>
        <span>${this.i18n.t('offline')}</span>
      `;
      this.container.insertBefore(indicator, this.messageList);
    } else if (this.isOnline && statusBar) {
      statusBar.remove();
    }
    if (this.button) {
      this.button.disabled = !this.isOnline || this.isLoading;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Keyboard navigation
  // ─────────────────────────────────────────────────────────────────────────

  _setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen && this.mode === 'widget') {
        this.toggleChatWindow();
      }
    });

    this.container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusable = this.container.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Human-agent polling
  // ─────────────────────────────────────────────────────────────────────────

  async _startPolling() {
    if (!this.pollingUrl || this._pollingTimer) return;
    this._lastMessageTimestamp = this._lastMessageTimestamp || new Date().toISOString();

    this._pollingTimer = setInterval(async () => {
      try {
        const url = new URL(this.pollingUrl);
        url.searchParams.set('sessionId', this.sessionId);
        url.searchParams.set('since', this._lastMessageTimestamp);

        if (this._agentMode) {
          const token = await this._generateEmbedToken();
          url.searchParams.set('embedToken', token);
        } else if (this.embedToken) {
          url.searchParams.set('embedToken', this.embedToken);
        }

        const resp = await fetch(url.toString());
        if (!resp.ok) return;

        const data = await resp.json();

        if (data.messages && data.messages.length > 0) {
          data.messages.forEach(msg => {
            if (msg.role === 'human_agent') {
              this.addMessage(msg.content, 'ai');
            }
          });
          const last = data.messages[data.messages.length - 1];
          this._lastMessageTimestamp = last.created_at;

          if (!this.isOpen) {
            this.unreadCount += data.messages.filter(m => m.role === 'human_agent').length;
            this._updateUnreadBadge();
          }
        }

        if (data.status === 'active' || data.status === 'resolved') {
          this._stopPolling();
        }
      } catch (_) {
        // Polling errors are non-fatal
      }
    }, this.pollingInterval);
  }

  _stopPolling() {
    if (this._pollingTimer) {
      clearInterval(this._pollingTimer);
      this._pollingTimer = null;
      this._showHandoffBanner(false);
    }
  }

  /**
   * Reset the current session — clears localStorage history, generates a fresh sessionId,
   * and re-displays the welcome message. The previous conversation remains in the server DB.
   * Called when the user clicks the "New conversation" button in the widget header.
   */
  _resetSession() {
    // Stop any active human-agent polling
    this._stopPolling();

    // Clear all localStorage keys tied to this agent/session
    try {
      localStorage.removeItem(this.historyKey);
      localStorage.removeItem(this.stateKey);
      if (this._agentMode) {
        localStorage.removeItem(`pindai-session-id-${this.agentId}`);
        localStorage.removeItem(`pindai-session-age-${this.agentId}`);
      }
    } catch (_) {}

    // Generate a fresh sessionId
    this.sessionId = `web-session-${Date.now()}-${Math.random()}`;
    if (this._agentMode) {
      try {
        localStorage.setItem(`pindai-session-id-${this.agentId}`, this.sessionId);
        localStorage.setItem(`pindai-session-age-${this.agentId}`, String(Date.now()));
      } catch (_) {}
    }

    // Reset bubble dismiss state so it can show again on next open
    this._bubbleDismissed = false;

    // Reset lead form state so it shows again for the new session
    this._leadDone = false;
    if (this._leadStateKey) {
      try { sessionStorage.removeItem(this._leadStateKey); } catch (_) {}
    }

    // Clear the message list DOM
    if (this.messageList) {
      this.messageList.innerHTML = '';
    }

    // Show welcome message for the new session
    if (this.initialMessage) {
      this.addMessage(this.initialMessage, 'ai');
    }

    // Re-render quick replies
    if (this.showQuickReplies && this.quickReplies.length > 0) {
      this._renderQuickReplies();
    }
  }

  /**
   * Show or hide the "Waiting for a human agent" banner above the message list.
   * @param {boolean} show - true to show, false to remove.
   */
  _showHandoffBanner(show) {
    if (!this.messageList) return;
    const parent = this.messageList.parentElement;
    if (!parent) return;
    const existing = parent.querySelector('.pindai-chat-handoff-banner');
    if (show && !existing) {
      const banner = document.createElement('div');
      banner.className = 'pindai-chat-handoff-banner';
      const pulse = document.createElement('span');
      pulse.className = 'pindai-chat-handoff-pulse';
      const text = document.createElement('span');
      text.textContent = this.i18n.t('waitingForAgent');
      banner.appendChild(pulse);
      banner.appendChild(text);
      parent.insertBefore(banner, this.messageList);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Legacy aliases (keep public API stable)
  // ─────────────────────────────────────────────────────────────────────────

  initLauncher() { return this._initLauncher(); }
  initChatWindow() { return this._initChatWindow(); }
  showTypingIndicator(show) { return this._showTypingIndicator(show); }
  handleFileSelect(e) { return this._handleFileSelect(e); }
  renderFilePreview(file) { return this._renderFilePreview(file); }
  sendMessageWithRetry(msg, files, retry) { return this._sendMessageWithRetry(msg, files, retry); }
  checkRateLimit() { return this._checkRateLimit(); }
  renderQuickReplies(replies) { return this._renderQuickReplies(replies); }
  incrementUnread() { return this._incrementUnread(); }
  updateUnreadBadge() { return this._updateUnreadBadge(); }
  clearUnreadCount() { return this._clearUnreadCount(); }
  setupOfflineDetection() { return this._setupOfflineDetection(); }
  updateOnlineStatus() { return this._updateOnlineStatus(); }
  setupKeyboardNavigation() { return this._setupKeyboardNavigation(); }
  startPolling() { return this._startPolling(); }
  stopPolling() { return this._stopPolling(); }
  formatTimestamp(d) { return this._formatTimestamp(d); }
  getDefaultIcon() { return this._getDefaultLauncherIcon(); }
  getErrorMessage(e) { return this._getErrorMessage(e); }
  delay(ms) { return this._delay(ms); }
}

// ───────────────────────────────────────────────────────────────────────────
// Global export
// ───────────────────────────────────────────────────────────────────────────

window.PindaiChatWidget = {
  init: (options) => {
    if (!document.querySelector('.n8n-chat-widget') && !document.querySelector('.n8n-chat-launcher')) {
      return new PindaiChatWidget(options);
    }
  }
};

// Backward compatibility
window.N8nChatWidget = window.PindaiChatWidget;
